import _CelebrationViewPageQuery from "../__generated__/CelebrationViewPageQuery.graphql";
import { Star } from 'lucide-react';
import { useEffect, useState } from 'react';
import { fetchQuery, graphql, useRelayEnvironment } from 'react-relay';
import { Link } from 'react-router-dom';
import { T } from '../i18n/RichText';
import { t } from '../i18n/t';
import { Sticker } from '../scrapbook/Sticker';
import { isStickerSlug, type StickerSlug } from '../scrapbook/stickers.ts';
import type { CelebrationViewPageQuery } from '../__generated__/CelebrationViewPageQuery.graphql';
const PageQuery = (_CelebrationViewPageQuery.hash && _CelebrationViewPageQuery.hash !== "a8e5accae66f80867f37c339c10c40c9" && console.error("The definition of 'CelebrationViewPageQuery' appears to have changed. Run `relay-compiler` to update the generated files to receive the expected data."), _CelebrationViewPageQuery);

/**
 * "A new page in your scrapbook!" (kid memory design §3.3), asked once on
 * mount -- which is once the session has finished, since that is when this
 * screen mounts. `endSession` and the story auto-end make the page before
 * they publish `SessionEnded`, so it is there to be read.
 *
 * `fetchQuery` rather than `useLazyLoadQuery`: a suspending query here would
 * blank the whole celebration while it loads, and a failed one would take
 * the screen down with it. This line is a bonus; without an answer it is
 * simply not drawn.
 */
function NewPage({
  sessionId
}: {
  sessionId: string;
}) {
  const environment = useRelayEnvironment();
  const [page, setPage] = useState<{
    id: string;
    sticker: StickerSlug;
  } | null>(null);
  useEffect(() => {
    const sub = fetchQuery<CelebrationViewPageQuery>(environment, PageQuery, {
      sessionId
    }, {
      fetchPolicy: 'network-only'
    }).subscribe({
      next: data => {
        const p = data.session?.scrapbookPage;
        if (p) setPage({
          id: p.id,
          sticker: isStickerSlug(p.sticker) ? p.sticker : 'chat'
        });
      },
      error: () => {}
    });
    return () => sub.unsubscribe();
  }, [environment, sessionId]);
  if (!page) return null;
  return <Link to={`/scrapbook?page=${encodeURIComponent(page.id)}`} className="btn btn-ghost btn-lg min-h-(--hit-min) h-auto gap-3 py-2 text-lg font-semibold">
      <Sticker slug={page.sticker} className="size-12 shrink-0" />
      {t('celebration-new-page')}
    </Link>;
}

/**
 * What a child sees when the session ends.
 *
 * Not the debrief: an analytical breakdown of a seven-year-old's errors is for
 * their parent, and §10 puts it behind the gate for that reason.
 *
 * Stars count turns taken, never accuracy. The pedagogy forbids signalling
 * error — §8.2 says the face must not leak what the words may not say — and a
 * star withheld for mistakes is that signal wearing a different hat. A child
 * would read it faster than any wording.
 */
export function CelebrationView({
  sessionId,
  turns,
  words,
  onAgain
}: {
  sessionId: string;
  turns: number;
  words: number;
  onAgain: () => void;
}) {
  const stars = Math.min(5, Math.max(1, Math.ceil(turns / 2)));
  return <div className="mx-auto flex max-w-md flex-col items-center gap-8 p-8 text-center bg-base-100 rounded-box shadow-lg">
      <div className="space-y-4">
        <h1 className="text-4xl font-bold">{t('celebration-title')}</h1>
        <p aria-label={t('celebration-stars', {
        stars
      })} className="flex gap-2 justify-center">
          {Array.from({
          length: 5
        }, (_, i) => <Star key={i} aria-hidden className={i < stars ? 'size-12 fill-current text-warning' : 'size-12 opacity-25'} />)}
        </p>
        <NewPage sessionId={sessionId} />
      </div>
      <div className="space-y-2">
        <p className="text-xl font-medium">
          <T k="celebration-words" params={{
          words
        }} parts={{
          strong: s => <strong>{s}</strong>
        }} />
        </p>
        <p className="text-base-content/80">
          <T k="celebration-turns" params={{
          turns
        }} parts={{
          strong: s => <strong>{s}</strong>
        }} />
        </p>
      </div>
      <button className="btn btn-primary min-h-(--hit-min) btn-lg" onClick={onAgain}>
        {t('celebration-again')}
      </button>
    </div>;
}